/* Copyright (c) 2019-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Pipe, PipeTransform, SecurityContext } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { invalidPipeArgumentError } from 'utils/lib/common/common-utils';

export type TrustAsHtmlFormat = 'html' | 'url' | 'resourceUrl';

// Only http(s) URLs may be marked as SafeResourceUrl.
const ALLOWED_RESOURCE_URL_SCHEMES = /^https?:\/\//i;

/**
 * Sanitizes content for safe DOM binding.
 *   - 'html' (default): Angular HTML sanitizer for [innerHTML].
 *   - 'url': Angular URL sanitizer for [href] / non-iframe [src].
 *   - 'resourceUrl': iframe [src] etc.; only http(s) URLs are trusted.
 */
@Pipe({ name: 'trustAsHtml', pure: true, standalone: true })
export class TrustAsHtmlPipe implements PipeTransform {
    constructor(private sanitizer: DomSanitizer) {}

    transform(content: unknown, format: TrustAsHtmlFormat | string = 'html'): string | SafeResourceUrl | null {
        if (content === null || content === undefined) {
            return null;
        }

        try {
            const value = typeof content === 'string' ? content : String(content);

            switch (format) {
                case 'url':
                    return this.sanitizer.sanitize(SecurityContext.URL, value);
                case 'resourceUrl':
                    return this.toSafeResourceUrl(value);
                case 'html':
                default:
                    return this.sanitizer.sanitize(SecurityContext.HTML, value);
            }
        } catch (error) {
            throw invalidPipeArgumentError(TrustAsHtmlPipe, error.message);
        }
    }

    private toSafeResourceUrl(value: string): SafeResourceUrl | null {
        if (!ALLOWED_RESOURCE_URL_SCHEMES.test(value)) {
            return null;
        }
        return this.sanitizer.bypassSecurityTrustResourceUrl(value);
    }
}
